import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import css from './reportModal.module.css';

const ReportModal = ({ isOpen, onClose, lesson }) => {
  const { t } = useTranslation();

  const CTX = {
    video: {
      label: 'Проблема з відео',
      main: lesson?.video?.name || 'Відео',
      meta: lesson?.name ? `Урок: ${lesson.name}` : '',
      cats: ['Відео не завантажується', 'Відео не відтворюється', 'Немає звуку', 'Субтитри не відображаються', 'Некоректні субтитри', 'Інше']
    },
    word: {
      label: 'Проблема зі словом',
      main: '',
      meta: lesson?.name ? `Урок: ${lesson.name}` : '',
      cats: ['Некоректний переклад', 'Переклад не відображається', 'Некоректне пояснення', 'Некоректний приклад', 'Не працює озвучення', 'Інше'],
      input: true,
      inputLabel: 'Яке слово?',
      inputPlaceholder: 'Впишіть слово…'
    },
    quiz: {
      label: 'Проблема із завданням',
      main: '',
      meta: lesson?.name ? `Тест · ${lesson.name}` : 'Тест',
      cats: ['Некоректне питання', 'Некоректна правильна відповідь', 'Некоректні варіанти', 'Помилка в перекладі', 'Завдання не працює', 'Інше'],
      input: true,
      inputLabel: 'Текст питання',
      inputPlaceholder: 'Опишіть або впишіть питання…'
    },
    speaking: {
      label: 'Проблема з розмовою з AI',
      main: '',
      meta: lesson?.name ? `Speaking · ${lesson.name}` : 'Speaking',
      cats: ['Не починається запис', 'Не зупиняється запис', 'Не відтворюється запис', 'AI неправильно розпізнав', 'AI некоректно оцінив', 'Некоректна підказка', 'Інше'],
      input: true,
      inputLabel: 'Фраза AI',
      inputPlaceholder: 'Впишіть фразу…'
    },
    other: {
      label: 'Інша проблема',
      main: '',
      meta: lesson?.name ? `Урок: ${lesson.name}` : '',
      cats: ['Технічна помилка', 'Сторінка не завантажується', 'Щось відображається некоректно', 'Інше']
    }
  };

  const [step, setStep] = useState(1); // 1: picker, 2: form, 3: success
  const [reportType, setReportType] = useState('');
  const [selCat, setSelCat] = useState(null);
  const [inputText, setInputText] = useState('');
  const [commentText, setCommentText] = useState('');

  // Reset state when opened
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setReportType('');
      setSelCat(null);
      setInputText('');
      setCommentText('');
    }
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  const openCtx = (type) => {
    setReportType(type);
    setSelCat(null);
    setStep(2);
  };

  const submitReport = () => {
    if (!selCat) return;
    
    const payload = {
      lessonId: lesson?.id,
      page: reportType,
      issueType: selCat,
      subject: inputText,
      comment: commentText,
      timestamp: new Date().toISOString()
    };
    
    console.log('📋 Report submitted (Mock):', payload);
    
    // Move to success step
    setStep(3);
    
    // Automatically close after a few seconds
    setTimeout(() => {
      handleClose();
    }, 3000);
  };

  if (!isOpen) return null;

  const currentCtx = reportType ? CTX[reportType] : null;

  return (
    <div className={`${css.rptOv} ${css.show}`} onClick={(e) => { if(e.target === e.currentTarget) handleClose(); }}>
      
      {/* STEP 1: PICKER */}
      {step === 1 && (
        <div className={`${css.rptMd} ${css.rptPicker}`}>
          <div className={css.rptHead}>
            <div className={css.rptTop}>
              <div className={css.rptTitle}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
                {t('reportProblemTitle', 'Повідомити про проблему')}
              </div>
              <div className={css.rptX} onClick={handleClose}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </div>
            </div>
            <div className={css.rptSub}>{t('reportProblemSub', 'Оберіть, з чим саме виникла проблема.')}</div>
          </div>
          <div className={css.rptBody} style={{ paddingBottom: '20px' }}>
            <div className={css.pkGrid}>
              <div className={css.pkCard} onClick={() => openCtx('video')}>
                <div className={`${css.pkIc} ${css.pkV}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg></div>
                <div className={css.pkBody}><div className={css.pkT}>{t('rptTypeVideo', 'Відео')}</div><div className={css.pkD}>{t('rptTypeVideoDesc', 'Не відтворюється, немає звуку або субтитрів.')}</div></div>
                <div className={css.pkArr}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg></div>
              </div>
              <div className={css.pkCard} onClick={() => openCtx('word')}>
                <div className={`${css.pkIc} ${css.pkW}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg></div>
                <div className={css.pkBody}><div className={css.pkT}>{t('rptTypeWord', 'Слово в уроці')}</div><div className={css.pkD}>{t('rptTypeWordDesc', 'Некоректний переклад, приклад або озвучення.')}</div></div>
                <div className={css.pkArr}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg></div>
              </div>
              <div className={css.pkCard} onClick={() => openCtx('quiz')}>
                <div className={`${css.pkIc} ${css.pkQ}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg></div>
                <div className={css.pkBody}><div className={css.pkT}>{t('rptTypeQuiz', 'Тест (Quiz)')}</div><div className={css.pkD}>{t('rptTypeQuizDesc', 'Помилка у питанні, відповідях або перекладі.')}</div></div>
                <div className={css.pkArr}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg></div>
              </div>
              <div className={css.pkCard} onClick={() => openCtx('speaking')}>
                <div className={`${css.pkIc} ${css.pkS}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/></svg></div>
                <div className={css.pkBody}><div className={css.pkT}>{t('rptTypeSpeaking', 'Розмова з AI')}</div><div className={css.pkD}>{t('rptTypeSpeakingDesc', 'Запис голосу, розпізнавання або оцінка AI.')}</div></div>
                <div className={css.pkArr}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg></div>
              </div>
              <div className={css.pkCard} onClick={() => openCtx('other')}>
                <div className={`${css.pkIc} ${css.pkO}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg></div>
                <div className={css.pkBody}><div className={css.pkT}>{t('rptTypeOther', 'Інша проблема')}</div><div className={css.pkD}>{t('rptTypeOtherDesc', 'Технічна помилка або щось інше.')}</div></div>
                <div className={css.pkArr}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* STEP 2: CONTEXTUAL FORM */}
      {step === 2 && currentCtx && (
        <div className={css.rptMd}>
          <div className={css.rptHead}>
            <div className={css.rptTop}>
              <div className={css.rptTitle}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
                {t('reportProblemTitle', 'Повідомити про проблему')}
              </div>
              <div className={css.rptX} onClick={handleClose}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </div>
            </div>
            <div className={css.rptSub}>{t('reportCheckWhat', 'Оберіть, що саме потрібно перевірити.')}</div>
            <div className={css.rptCtx}>
              <div className={css.rptCtxLabel}>{currentCtx.label}</div>
              {currentCtx.main && <div className={css.rptCtxMain}>{currentCtx.main}</div>}
              <div className={css.rptCtxMeta}>{currentCtx.meta}</div>
            </div>
          </div>
          <div className={css.rptBody}>
            <div className={css.rptCl}>{t('reportWhatsWrong', 'Що саме не так?')}</div>
            <div className={css.rptCats}>
              {currentCtx.cats.map((cat, index) => (
                <div 
                  key={index} 
                  className={`${css.rptCat} ${selCat === cat ? css.sel : ''}`} 
                  onClick={() => setSelCat(cat)}
                >
                  <div className={css.rptCatR}></div>{cat}
                </div>
              ))}
            </div>
            {currentCtx.input && (
              <div className={css.rptWinput}>
                <label>{currentCtx.inputLabel}</label>
                <input 
                  placeholder={currentCtx.inputPlaceholder} 
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                />
              </div>
            )}
            <div className={css.rptTa}>
              <label>{t('reportCommentOpt', 'Коментар — необов\'язково')}</label>
              <textarea 
                placeholder={t('reportCommentPlace', 'Опишіть проблему коротко…')}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
              />
            </div>
          </div>
          <div className={css.rptFoot}>
            <button className={`${css.rptBtn} ${css.rptBtnS}`} onClick={() => setStep(1)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>
              {t('back', 'Назад')}
            </button>
            <button 
              className={`${css.rptBtn} ${css.rptBtnP} ${!selCat ? css.dis : ''}`} 
              onClick={submitReport}
            >
              {t('send', 'Надіслати')} <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: SUCCESS */}
      {step === 3 && (
        <div className={css.rptMd}>
          <div className={`${css.rptSuc} ${css.show}`}>
            <div className={css.rptSucIc}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
            </div>
            <h3>{t('thankYou', 'Дякуємо!')}</h3>
            <p>{t('reportReceived', 'Ми отримали повідомлення та перевіримо проблему.')}</p>
            <button className={`${css.rptBtn} ${css.rptBtnP}`} style={{ margin: '0 auto' }} onClick={handleClose}>
              {t('close', 'Закрити')}
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default ReportModal;
